import { getEnvironmentActivitiesRequest } from "Api/activity/getEnvironmentActivities.request";
import { getEnvironmentTasksRequest } from "Api/organizations/projects/environments/tasks/getEnvironmentTasks.request";
import { createApiErrorPayload } from "src/common/utils/apiErrorHandling";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

import { TASK_ACTIVITY_WINDOW } from "../constants";
import { getEnvironmentTasksKey } from "../utils";

import type { EnvironmentTasksParams } from "../types";
import type { ActivityTypeFilters } from "Api/activity/types";

/** The single activity type that carries task run status + last-run. */
const TASK_ACTIVITY_TYPE = "environment.task" satisfies ActivityTypeFilters;

/**
 * Loads the task-container data layer for one environment in a single pass:
 *  - definitions from `GET .../tasks` (authoritative list, incl. never-run)
 *  - a window of `environment.task` activities (status + last-run source)
 *
 * The two are fetched in parallel; `selectTaskContainerRows` joins them with the
 * deployment topology for the allocation size.
 */
export const fetchEnvironmentTasksThunk = createAppAsyncThunk(
  "environmentTasks/fetch",
  async (params: EnvironmentTasksParams, { rejectWithValue }) => {
    const { projectId, environmentId } = params;
    const encodedEnvId = encodeURIComponent(environmentId);

    try {
      const [definitions, activities] = await Promise.all([
        getEnvironmentTasksRequest({ projectId, encodedEnvId }),
        getEnvironmentActivitiesRequest({
          projectId,
          encodedEnvId,
          filters: [TASK_ACTIVITY_TYPE],
          count: TASK_ACTIVITY_WINDOW
        })
      ]);

      return {
        key: getEnvironmentTasksKey(params),
        definitions: definitions ?? [],
        activities: activities ?? []
      };
    } catch (error) {
      return rejectWithValue(createApiErrorPayload(error));
    }
  }
);
